/* @flow */
import humanizeDuration from 'humanize-duration';
import extend from 'lodash/extend';

import moment from 'moment-timezone';
import { DateRange } from 'moment-range';

import dayjs from 'dayjs';
import isYesterday from 'dayjs/plugin/isYesterday';
import I18nHelper from 'common/helpers/I18nHelper';
dayjs.extend(isYesterday);

export const DateTimeFormat = {
    HUMAN_DATE_FORMAT: 'DD-MM-YYYY',
    SHEET_DATE_FORMAT: 'DD/MM/YY',
    SHEET_DATE_FORMAT_WITH_TIME: 'hh:mm A DD/MM/YY',
    SHEET_DATE_FORMAT_WITH_TIME_24_HOUR: "HH:mm, DD MMM [']YY",
    SHORT_DATE_FORMAT: 'DD/MM',
    FANCY_DATE_FORMAT: 'DD MMM',
    LONG_DATE_FORMAT: 'YYYY-MM-DD',
    LONG_DATE_WITH_TIME: 'YYYY-MM-DD HH:mm',
    FULL_FANCY_DATE_FORMAT: 'DD MMM YYYY',
    FULL_FANCY_DATE_FORMAT_WITH_TIME: 'hh:mm A DD MMM YY',
    HUMAN_COMPACT_DATE_FORMAT: "Do MMM [']YY",
    HUMAN_TIME_FORMAT: 'hh:mm a',
    FANCY_TIME_FORMAT: 'h:mm A',
    FANCY_COMPACT_TIME_FORMAT: 'h:mmA',
    MILITARY_TIME_FORMAT: 'HH:mm:ss',
    SHORT_ISO_FORMAT: 'YYYY-MM-DDTHH:mm:ss',
    HTML5_FMT_TIME: 'HH:mm',
    HUMAN_DATE_TIME_FORMAT: "D MMM 'YY hh:mm A",
    UTC_FORMAT: 'YYYY-MM-DDTHH:mm:ssZZ',
    UTC_FORMAT_WITH_MILLIS: 'YYYY-MM-DDTHH:mm:ss.SSSZZ',
    UTC_FORMAT_WITH_Z: 'YYYY-MM-DDTHH:mm:ss.SSS[Z]',
    HUMAN_DATE_FORMAT_MONTH_FIRST: 'MM-DD-YYYY',
    SHEET_DATE_FORMAT_MONTH_FIRST: 'MM/DD/YY',
    HUMAN_COMPACT_DATE_FORMAT_MONTH_FIRST: "MMM Do [']YY",
    FULL_FANCY_DATE_FORMAT_MONTH_FIRST: 'MMM DD YYYY',
    HUMAN_DATE_TIME_FORMAT_MONTH_FIRST: "MMM D 'YY hh:mm A",
    HUMAN_DATE_TIME_FORMAT_WITH_COMMA: 'D MMM, YYYY h:mm A',
    HUMAN_DATE_TIME_FORMAT_WITH_COMMA_MONTH_FIRST: 'MMM D, YYYY h:mm A',
    SHEET_DATE_FORMAT_YYYY: 'DD/MM/YYYY',
    SHEET_DATE_FORMAT_YYYY_MONTH_FIRST: 'MM/DD/YYYY',
    FORMAT_Do_MMMM_YYYY: 'Do MMMM YYYY',
    FORMAT_Do_MMMM_YYYY_MONTH_FIRST: 'MMMM Do YYYY',
    FORMAT_Do_MMM_YYYY: 'Do MMM YYYY',
    FORMAT_Do_MMM_YYYY_MONTH_FIRST: 'MMM Do YYYY',
    FORMAT_Do_MMM: 'Do MMM',
    FORMAT_Do_MMM_MONTH_FIRST: 'MMM Do'
};

export const TimeUnits = {
    h: 'hours',
    s: 'seconds',
    m: 'minutes',
    d: 'day'
};

const translationsNamespace = ['translations'];
const englishLocale = 'en-US';

const getLanguageForHumanizeDuration = () => {
    return humanizeDuration.getSupportedLanguages().includes(moment.locale())
        ? moment.locale()
        : 'en';
};

const nsTranslate = I18nHelper.translate.bind(null, translationsNamespace);
class TimeUtils {
    /**
     * Gets the local date/time from the given moment &
     * converts it to a different timezone.
     * Uses moment.tz(..., timezone)
     */
    static convertToTimezone(momentObj: moment, timezone: string) {
        const localTimeString = momentObj.format(
            DateTimeFormat.SHORT_ISO_FORMAT
        );
        return moment.tz(localTimeString, timezone);
    }

    static humanizeSeconds(timeInSeconds, options) {
        const {
            round,
            units,
            format,
            largest,
            shorten,
            spacer,
            conjunction
        } = {
            round: true,
            units: ['h', 'm', 's'],
            format: null,
            largest: 1,
            shorten: false,
            spacer: ' ',
            ...options
        };
        const customLanguage = format;
        const personnelLanguage = getLanguageForHumanizeDuration();
        let shortLanguage;
        if (personnelLanguage === 'en' && shorten && !customLanguage) {
            shortLanguage = {
                d: c => {
                    return c > 1 ? 'd' : 'd';
                },
                h: c => {
                    return c > 1 ? 'hrs' : 'hr';
                },
                m: c => {
                    return c > 1 ? 'min' : 'min';
                },
                s: () => {
                    return 's';
                }
            };
        }
        const humanizer = humanizeDuration.humanizer({
            units,
            largest,
            round,
            spacer,
            conjunction,
            language: getLanguageForHumanizeDuration()
        });
        if (customLanguage) {
            humanizer.languages.customLanguage = customLanguage;
            humanizer.language = 'customLanguage';
        }
        if (shortLanguage) {
            humanizer.languages.shortLanguage = shortLanguage;
            humanizer.language = 'shortLanguage';
        }
        var humanized = humanizer(timeInSeconds * 1000);
        return humanized;
    }

    static now(isFormatted, defaultFormat = 'YYYY-MM-DD'): moment {
        if (isFormatted) {
            return TimeUtils.format(moment(), defaultFormat);
        }
        return moment();
    }

    static humanizeMillis(timeInMillis) {
        var humanized = humanizeDuration(timeInMillis, {
            units: ['h', 'm', 's'],
            largest: 1,
            round: true,
            language: getLanguageForHumanizeDuration()
        });
        return humanized;
    }

    static simpleHumanize(timeInSeconds, options) {
        const defaultOptions = { unit: 'h', decimals: 2 };
        const { unit, decimals } = extend(defaultOptions, options);
        let divider;
        let unitString;
        let hideUnit = false;
        switch (unit) {
            case 'h':
                unitString = 'hours';
                divider = 60 * 60;
                break;
            case 'm':
                unitString = 'minutes';
                divider = 60;
                break;
            case 's':
                unitString = 'seconds';
                divider = 1;
                break;
            default:
                throw new Error(`Unknown unit: ${unit}`);
        }
        if (options) {
            unitString = options.unitString || unitString;
            ({ hideUnit } = options);
        }
        const result = parseFloat(timeInSeconds / divider).toFixed(decimals);
        return `${result} ${!hideUnit ? unitString : ''}`;
    }

    static formatTimeOnly(
        isoDateTime,
        timeFormat = 'h:mm a',
        placeHolderText = ''
    ) {
        if (isoDateTime) {
            var momentDate = moment(isoDateTime);
            if (momentDate.isValid()) {
                return momentDate.format(timeFormat);
            }
        }
        return placeHolderText;
    }

    static format(
        date,
        dateFormat,
        placeholderText = null,
        { inputFormat, strict } = { strict: false },
        locale
    ) {
        if (date) {
            var momentDate = TimeUtils.getMomentWithLocale(
                date,
                locale,
                inputFormat,
                strict
            );

            if (momentDate.isValid()) {
                return TimeUtils.getMomentWithLocale(momentDate, locale).format(
                    dateFormat
                );
            }
        }
        return placeholderText;
    }

    static parse(date, time) {
        return TimeUtils.parseDateTime(`${date} ${time}`);
    }

    static parseDateTime(dateTime, format = 'YYYY-MM-DD HH:mm') {
        return moment(dateTime, format);
    }

    static parseDateTimeWithMeridiem(date, time, locale) {
        return TimeUtils.getMomentWithLocale(
            `${date} ${time}`,
            locale,
            'YYYY-MM-DD H:mm a'
        );
    }

    /**
     * Converts an ISO DateTime String to a shift Instant in relation to the given date and time
     *
     * @param dateString ISO DateTime String
     * @param expectedDate ISO Date format (YYYY-MM-DD) of the expected Date
     * @returns {{day: *, time: *}}
     */
    static isoDateToShiftInstant(dateString, expectedDate, locale) {
        var timeMoment = TimeUtils.getMomentWithLocale(dateString, locale);

        var expectedDateMoment = TimeUtils.getMomentWithLocale(
            `${expectedDate}`,
            locale,
            'YYYY-MM-DD'
        );

        return {
            day: timeMoment
                .clone()
                .startOf('day')
                .diff(expectedDateMoment, 'days'),
            time: timeMoment.format('h:mm a')
        };
    }

    static shiftInstantToIsoDate(shiftInstant, expectedDate, locale) {
        return TimeUtils.getMomentWithLocale(
            `${expectedDate} ${shiftInstant.time}`,
            locale,
            'YYYY-MM-DD h:mm a'
        ).add(shiftInstant.day, 'days');
    }

    static isSame(start, end, unitString = '') {
        const startMoment = moment(start);
        const endMoment = moment(end);

        return startMoment.isSame(endMoment, unitString);
    }

    static isSameOrBefore(start, end, unitString = '') {
        const startMoment = moment(start);
        const endMoment = moment(end);

        return startMoment.isSameOrBefore(endMoment, unitString);
    }

    static isSameOrAfter(start, end, unitString = '') {
        const startMoment = moment(start);
        const endMoment = moment(end);

        return startMoment.isSameOrAfter(endMoment, unitString);
    }

    static isAfter(start, end, unitString = '') {
        const startMoment = moment(start);
        const endMoment = moment(end);

        return startMoment.isAfter(endMoment, unitString);
    }

    static isAfterWithFormat({
        startTime,
        endTime,
        format = DateTimeFormat.HTML5_FMT_TIME
    }) {
        return moment(startTime, format).isAfter(moment(endTime, format));
    }

    static formatTime(
        timeString,
        expectedDateMoment,
        customOutputFormats,
        locale,
        isDateFormatMMDDYYYY?: boolean = false
    ) {
        if (!timeString) {
            return '';
        }

        /*----------  moment output formats  ----------*/
        const timeFormat = customOutputFormats?.timeFormat ?? 'LT'; // 12:00 PM, use for only time
        // other fields, used for date + time
        const monthFormat = 'MMM'; // Jan
        const hourFormat = 'h:mm A'; // 12:00 PM
        const dateFormat = 'D'; // 1

        let defaultFormat = isDateFormatMMDDYYYY
            ? DateTimeFormat.HUMAN_DATE_TIME_FORMAT_WITH_COMMA_MONTH_FIRST
            : DateTimeFormat.HUMAN_DATE_TIME_FORMAT_WITH_COMMA;

        if (customOutputFormats?.defaultFormat) {
            ({ defaultFormat } = customOutputFormats);
        }

        const timeMoment = TimeUtils.getMomentWithLocale(timeString, locale);
        if (expectedDateMoment) {
            if (timeMoment.isSame(expectedDateMoment, 'day')) {
                // if same day, format & show just time, eg "12:00PM"
                return timeMoment.format(timeFormat);
            } else if (timeMoment.isSame(expectedDateMoment, 'year')) {
                // if same year, show date+time, eg "1st Jan, 12:00 PM"
                const date = timeMoment.format(dateFormat);
                const month = timeMoment.format(monthFormat);
                const time = timeMoment.format(hourFormat);
                const dateTimeFormat = isDateFormatMMDDYYYY
                    ? `${month} ${date}, ${time}`
                    : `${date} ${month}, ${time}`;
                return dateTimeFormat;
            }
        }
        return timeMoment.format(defaultFormat);
    }

    static formatTimeRange(
        start,
        end,
        expectedDate,
        forceShowDate = false,
        customOutputFormats,
        locale
    ) {
        const startMoment = TimeUtils.getMomentWithLocale(start, locale);
        const endMoment = TimeUtils.getMomentWithLocale(end, locale);
        const shouldShowDate = forceShowDate;

        if (shouldShowDate) {
            // since force show date should not have any comparisons: It should only print the input date.
            return `${TimeUtils.formatTime(
                start,
                null,
                customOutputFormats,
                locale
            )} - ${TimeUtils.formatTime(
                end,
                null,
                customOutputFormats,
                locale
            )}`;
        }

        if (expectedDate) {
            var expectedDateMoment = TimeUtils.getMomentWithLocale(
                expectedDate,
                locale
            );

            if (
                !startMoment.isSame(endMoment, 'day') &&
                !startMoment.isSame(expectedDateMoment, 'day')
            ) {
                return `${TimeUtils.formatTime(
                    start,
                    null,
                    customOutputFormats,
                    locale
                )} - ${TimeUtils.formatTime(
                    end,
                    start,
                    customOutputFormats,
                    locale
                )}`;
            }
        }

        return `${TimeUtils.formatTime(
            start,
            expectedDateMoment,
            customOutputFormats,
            locale
        )} - ${TimeUtils.formatTime(
            end,
            expectedDateMoment,
            customOutputFormats,
            locale
        )}`;
    }

    static formatDateTime({ date, dateFormat, timeFormat }) {
        const formattedDate = TimeUtils.format(date, dateFormat);
        const formattedTime = TimeUtils.format(date, timeFormat);
        return { date: formattedDate, time: formattedTime };
    }

    static formatDateRange({
        startDate,
        endDate,
        singleDateFormat = 'Do MMM YYYY, ddd',
        startDateFormat = 'Do MMM',
        endDateFormat = "Do MMM [']YY"
    }) {
        let dateRange = '';
        if (startDate.isSame(endDate, 'day') || !endDate) {
            dateRange = TimeUtils.format(startDate, singleDateFormat);
        } else if (startDate && endDate) {
            dateRange = `${TimeUtils.format(
                startDate,
                startDateFormat
            )} – ${TimeUtils.format(endDate, endDateFormat)}`;
        }
        return dateRange;
    }

    static getFormattedDate(date: Moment): string {
        return TimeUtils.format(date, DateTimeFormat.LONG_DATE_FORMAT);
    }

    static getDateRangeAsCSV(startDate, endDate, defaultFormat = 'YYYY-MM-DD') {
        const formattedDates = [];
        // separated into different if blocks to aid in narrowing down the error.
        if (!moment(startDate).isValid()) {
            throw new Error('Start Date is invalid.');
        }
        if (!moment(endDate).isValid()) {
            throw new Error('End Date is invalid.');
        }
        const localStartDate = startDate.clone();
        const localEndDate = endDate.clone();

        if (TimeUtils.isSame(localStartDate, localEndDate, 'day')) {
            return TimeUtils.formatWithEnglishLocale(
                localStartDate,
                defaultFormat
            );
        }

        while (!TimeUtils.isAfter(localStartDate, localEndDate, 'days')) {
            formattedDates.push(
                TimeUtils.formatWithEnglishLocale(localStartDate, defaultFormat)
            );
            localStartDate.add(1, 'day');
        }
        return formattedDates.join();
    }

    static isValidDate(date, format = 'YYYY-MM-DD', strict = true) {
        // check by default for ISO format
        return moment(date, format, strict).isValid();
    }

    static diff({ startDate, endDate, unit = 'seconds' }) {
        return moment(endDate).diff(moment(startDate), unit);
    }

    static timeDiff({ startDate, endDate }) {
        return moment.duration(startDate.diff(endDate)).asSeconds();
    }

    static timeDiffMinutes({
        startTime,
        endTime,
        format = DateTimeFormat.HTML5_FMT_TIME
    }: {
        startTime: string,
        endTime: string,
        format: ?string
    }): boolean {
        return moment(endTime, format).diff(
            moment(startTime, DateTimeFormat.HTML5_FMT_TIME),
            'minutes'
        );
    }

    static fromNow(date) {
        const isValidDate = TimeUtils.isValidDate(date, '', false);
        if (!isValidDate) {
            throw new Error('Invalid Date');
        }

        return date.fromNow();
    }

    static daysFromNow(date) {
        const isValidDate = TimeUtils.isValidDate(date, '', false);
        if (!isValidDate) {
            throw new Error('Invalid Date');
        }
        const today = moment().startOf('day');

        return Math.round(moment.duration(today - date).asDays());
    }

    static timeUnitsPassed(datetime, unit) {
        const isValidDate = TimeUtils.isValidDate(datetime, '', false);
        if (!isValidDate) {
            throw new Error('Invalid Date');
        }

        return moment().diff(datetime, unit);
    }

    static startOf({ date, unit = TimeUnits.d }) {
        const isValidDate = TimeUtils.isValidDate(date, '', false);
        const isValidUnit = moment.normalizeUnits(unit);

        if (!isValidDate || !isValidUnit) {
            throw new Error('Invalid Date');
        }

        return moment(date).startOf(unit);
    }

    static endOf({ date, unit = TimeUnits.d }) {
        const isValidDate = TimeUtils.isValidDate(date, '', false);
        const isValidUnit = moment.normalizeUnits(unit);

        if (!isValidDate || !isValidUnit) {
            throw new Error('Invalid Date');
        }

        return moment(date).endOf(unit);
    }

    static add({ date, period, unit = 'days' }) {
        return moment(date).add(period, unit);
    }

    static subtract({ date, period, unit = 'days' }) {
        return moment(date).subtract(period, unit);
    }
    static getMoment(dateString, inputFormat?: $Values<typeof DateTimeFormat>) {
        if (!dateString) {
            return '';
        }
        if (inputFormat) {
            return moment(dateString, inputFormat);
        }
        return moment(dateString);
    }

    static getDayShortCode(dayOfWeek) {
        return nsTranslate(`daysShortCode.${dayOfWeek}`);
    }

    /**
     * It returns moment object with locale
     * By default: locale is client configured locale
     */
    static getMomentWithLocale(
        date,
        locale = moment.locale(),
        inputFormat,
        strict = false
    ) {
        if (date) {
            if (inputFormat) {
                if (strict) {
                    return moment(date, inputFormat, locale, strict);
                }
                return moment(date, inputFormat, locale);
            }
            return moment(date).locale(locale);
        }
        return moment().locale(locale);
    }

    /**
     * Moment functions with english locale
     * Used in places where we need values in english locale independent
     * of the personnel language of user e.g: values sending in api, queryParams, etc..
     */
    static getMomentWithEnglishLocale(date, inputFormat, strict = false) {
        return TimeUtils.getMomentWithLocale(
            date,
            englishLocale,
            inputFormat,
            strict
        );
    }

    static formatWithEnglishLocale(date, format = 'YYYY-MM-DD') {
        return TimeUtils.getMomentWithEnglishLocale(date).format(format);
    }

    static parseDateTimeWithMeridiemWithEnglishLocale(date, time) {
        return TimeUtils.parseDateTimeWithMeridiem(date, time, englishLocale);
    }

    static formatTimeRangeWithEnglishLocale(
        start,
        end,
        expectedDate,
        forceShowDate = false,
        customOutputFormats
    ) {
        return TimeUtils.formatTimeRange(
            start,
            end,
            expectedDate,
            forceShowDate,
            customOutputFormats,
            englishLocale
        );
    }

    static shiftInstantToIsoDateWithEnglishLocale(shiftInstant, expectedDate) {
        return TimeUtils.shiftInstantToIsoDate(
            shiftInstant,
            expectedDate,
            englishLocale
        );
    }

    static isoDateToShiftInstantWithEnglishLocale(dateString, expectedDate) {
        return TimeUtils.isoDateToShiftInstant(
            dateString,
            expectedDate,
            englishLocale
        );
    }

    static formatWithEnglishLocaleInStrictMode(
        date,
        dateFormat = DateTimeFormat.HUMAN_DATE_FORMAT,
        placeholderText = null,
        { inputFormat } = { inputFormat: [DateTimeFormat.HUMAN_DATE_FORMAT] }
    ) {
        return TimeUtils.format(
            date,
            dateFormat,
            placeholderText,
            { inputFormat, strict: true },
            englishLocale
        );
    }

    static isOverlappingDateRanges(
        dateRange1: [Date, Date],
        dateRange2: [Date, Date]
    ): boolean {
        const range1 = new DateRange(dateRange1);
        const range2 = new DateRange(dateRange2);

        return range1.overlaps(range2);
    }

    static getToday(format = DateTimeFormat.LONG_DATE_FORMAT) {
        return moment().format(format);
    }

    static getUtcValue(
        dateTime: string,
        format: string = DateTimeFormat.UTC_FORMAT_WITH_Z
    ): string {
        return moment.utc(dateTime).format(format);
    }

    static getUtcFormattedTime(momentObj: moment) {
        return momentObj.utc().format(DateTimeFormat.UTC_FORMAT);
    }

    static getUtcTimeWithMillis(momentObj: moment) {
        const clonedObj = momentObj.clone(); // avoid mutation
        return clonedObj.utc().format(DateTimeFormat.UTC_FORMAT_WITH_MILLIS);
    }

    static getUtcTimeWithMillisEnglishLocale(momentObj: moment) {
        const clonedObj = momentObj.clone(); // avoid mutation
        return TimeUtils.getMomentWithEnglishLocale(clonedObj)
            .utc()
            .format(DateTimeFormat.UTC_FORMAT_WITH_MILLIS);
    }

    static isToday(date) {
        const today = TimeUtils.now();
        const isToday = TimeUtils.isSame(date, today, 'day');
        return isToday;
    }

    static isYesterday(date: string | Date): boolean {
        const dayjsObject = dayjs(date);
        return dayjsObject.isYesterday();
    }

    static formatDuration(durationInMillis: number, format: string) {
        return moment.utc(durationInMillis).format(format);
    }

    static getDuration(
        start: string,
        end: string,
        format?: string = 'HH:mm'
    ): number {
        if (!end || !start) {
            throw Error('start or end time cannot be empty');
        }
        // start time and end time
        const startTime = moment(start, format);
        const endTime = moment(end, format);

        if (startTime.isAfter(endTime)) {
            const dayOneDuration = TimeUtils.getDuration(start, '23:59');
            const dayTwoDuration = TimeUtils.getDuration('00:00', end);
            const additionalSeconds = 60; // additional one minute to cover '23:59' to '00:00
            return dayOneDuration + dayTwoDuration + additionalSeconds;
        } else {
            return moment.duration(endTime.diff(startTime)).asSeconds();
        }
    }

    static getDayFromDateEnglish(date: string): Day {
        return moment(date).locale('en-US').format('dddd');
    }
}

export default TimeUtils;
